import { z } from 'zod';
import { noyauAnalysisSchema } from './analysis.js';
import { analystPassSchema } from './analyst.js';
import { experienceLocalIdSchema } from './experience.js';
import { analystSeatKeySchema } from './models.js';
import {
  NOYAU_CODES,
  noyauCardIdSchema,
  noyauChoiceCardsSchema,
  noyauChoiceModeSchema,
  noyauCodeSchema,
  noyauDeterminationStateSchema,
  noyauDrawSchema,
  noyauMaxGapSchema,
  noyauScoreSchema,
} from './noyau.js';
import { turnCallSchema, turnPromptSchema } from './turn.js';
import { versionTagSchema } from './versioning.js';

/**
 * What a SESSION records of the noyau (noyau.md §8, N14) - at the régime of
 * §8.7 of the refonte, because none of it can be rebuilt afterwards: the notes a
 * model gave, the draw that broke a tie, the order two cards were shown in, the
 * card a person took.
 *
 * These are the STORED shapes of the tables the contract of issue #147 poses
 * (`packages/backend/src/db/schema.ts`), and the reading the lots of Sessions
 * and of the MCP (#152) serve. The determination is written since #149
 * (`packages/backend/src/session/noyau.ts`), the choices since #151
 * (`packages/backend/src/session/noyau-choices.ts`).
 *
 * None is `.strict()`, like every stored shape (§4.1): a field added later must
 * not make a row already written unreadable - and every field added to one of
 * them carries a read default, the régime of BUILD-787.
 */

/**
 * The raw answer of the analyst, bounded where it is recorded - a defect meets
 * it, never a model on purpose: 131 072 characters, since #196.
 *
 * Raised from 20 000, which the second pass can pass on a good day: it notes up
 * to six candidates, each with eight passages for and eight against copied word
 * for word - six times (16 passages of 150 to 500 characters, plus a sentence and
 * the keys, about 700) is 20 000 to 55 000 characters, and the bound CLAMPS
 * silently (`storedAnalystOutput`), taking the tail of an answer that was good.
 * What an answer can never be is longer than the seat lets the model write: 32 768
 * tokens, the thinking included (noyau.md §4.3), at four characters a token at
 * the very most, 131 072. No answer the seat can write is ever clamped, and the
 * bound still stops a defect.
 */
export const NOYAU_ANALYST_OUTPUT_MAX = 131_072;

/**
 * At most this many calls on one determination: one call and ONE retry per
 * pass, whatever the failure (N4; analyste.md §5.3, §7.5) - two passes since
 * #196, so four. A bound and not a promise, like `TURN_CALLS_MAX`.
 */
export const NOYAU_ANALYST_CALLS_MAX = 4;

/** The bound of a recorded prompt version - a handle like `1`, met by a defect, never on purpose. */
export const NOYAU_ANALYST_PROMPT_VERSION_MAX = 64;

/**
 * THE FINGERPRINT of the system an analyst's call was served (noyau.md §4.9,
 * N39; issue #154): the first 8 hexadecimal characters of the SHA-256 of that
 * system, in UTF-8.
 *
 * Two calls of one fingerprint received exactly the same instrument: a protocol
 * version that does not touch its reading keeps the fingerprint of the one
 * before, and two protocols that share a reading are grouped together. It is
 * what the measure of §13 reads « une version du prompt » on, now that a
 * version of the reading is a protocol version and no longer a merge request.
 * Computed by the server, which alone holds a hash function; eight characters
 * are plenty for the handful of readings one team writes, and short enough to
 * print in the parcours.
 */
export const noyauPromptFingerprintSchema = z.string().regex(/^[0-9a-f]{8}$/);
export type NoyauPromptFingerprint = z.infer<typeof noyauPromptFingerprintSchema>;

/**
 * ONE call of the analyst (§8), « comme un appel de tour »: the seat, the model
 * the API ANSWERED, the four token buckets, the cost at the seat's tariff, the
 * duration, the start, the failure if any and the provider's stop reason - the
 * fields of `turnCallSchema`, whose notes say why each one is there.
 *
 * Its own shape and not a widened `turnSeatSchema`: that enum names what a TURN
 * did, and a turn never calls the analyst. Three fields are added. Two because a
 * determination is also retried on an answer of the wrong SHAPE (N4) and « la
 * cause de chaque essai » (§9.1) must be readable for it: the raw `output` the
 * model returned, and whether it was `malformed` - failing
 * `noyauAnalystOutputSchema` - on a call that itself succeeded. The third and
 * the fourth, `promptVersion` and `promptFingerprint`, because the measure of
 * §13 is read BY VERSION OF THE PROMPT, through the MCP, which reads the base
 * and not the traces.
 */
export const noyauAnalystCallSchema = turnCallSchema.extend({
  seat: analystSeatKeySchema,
  /** What the model answered, verbatim - the one attempt nobody can rebuild is the malformed one. */
  output: z.string().max(NOYAU_ANALYST_OUTPUT_MAX).default(''),
  /** The call returned, but its answer did not parse as six notes. */
  malformed: z.boolean().default(false),
  /**
   * The version of the ASSEMBLY written in code this call was served -
   * `ANALYST_PROMPT_VERSION` of the backend, `2` since #154, bumped when the
   * framing, the grid, the heading of the reading or the contract change; `3`
   * for the two passes of #196 (`ANALYST_TWO_PASS_PROMPT_VERSION`). Null on a
   * call recorded before the field existed, read by default at the régime of
   * BUILD-787: a stored determination of before never stops reading.
   */
  promptVersion: z.string().min(1).max(NOYAU_ANALYST_PROMPT_VERSION_MAX).nullable().default(null),
  /**
   * The fingerprint of the system this call was served (§4.9, N39): what tells
   * two readings apart, where `promptVersion` only tells two assemblies. The
   * retry keeps the system, so it keeps the fingerprint. Null on a call
   * recorded before #154, read by default like `promptVersion`.
   */
  promptFingerprint: noyauPromptFingerprintSchema.nullable().default(null),
  /**
   * The PASS this call belongs to (analyste.md §10): 1 for the functions of each
   * objective, 2 for the style of each candidate - a retry keeps the pass of the
   * call it repeats. Null on a call recorded before #196, which had no passes,
   * read by default like the two fields above.
   */
  pass: analystPassSchema.nullable().default(null),
});
export type NoyauAnalystCall = z.infer<typeof noyauAnalystCallSchema>;

/**
 * What the analyst was SENT on one call, exactly as it left the server (§8) -
 * « l'entrée servie », in its own table and read on a click, at the régime of
 * `session_turn_prompts`: it carries the person's matter a second time, so it is
 * purged with the session, and it is the largest and least read thing a
 * determination produces.
 */
export const noyauAnalystPromptSchema = turnPromptSchema.extend({
  seat: analystSeatKeySchema,
  /**
   * The pass this entry was served for (analyste.md §10): since #196 a
   * determination serves two entries, one per pass, each marked. Null on an entry
   * written before, which served the one analyst there was.
   */
  pass: analystPassSchema.nullable().default(null),
});
export type NoyauAnalystPrompt = z.infer<typeof noyauAnalystPromptSchema>;

/**
 * `GET /api/sessions/{id}/noyau/prompts` (issue #152) - what the analyst was
 * served, call by call, in the order of `calls`: read on a CLICK and never with
 * the session, at the régime of `turnPromptsSchema` (§8). Empty while the
 * determination is pending, on `no-extracts` - which served nothing - and on a
 * session that has no determination at all.
 */
export const noyauAnalystPromptsSchema = z.object({
  prompts: z.array(noyauAnalystPromptSchema).default([]),
});
export type NoyauAnalystPrompts = z.infer<typeof noyauAnalystPromptsSchema>;

/**
 * THE determination of a session (§4, §8): one per session, computed ONCE,
 * stored, and never recomputed - a reprise finds the same (§4.4).
 *
 * - `state` - `pending` from its launch - on entering the farewell step, or at
 *   the close of a protocol without one (`noyau.md` §4.4) - then one of the
 *   three settled states (§4.5).
 * - `scores` - the six notes and their justifications, in the order the model
 *   gave them; empty until `rendered`, and empty on `no-extracts` and `failed`.
 * - `principal`, `second` - what the RANKING made of them (`rankNoyaux`); null
 *   unless `rendered`.
 * - `draw` - the tie the draw settled, if one decided a place (N2).
 * - `calls` - every call of the analyst, the retry included: none on
 *   `no-extracts`, which passes no call at all.
 * - `costEur` - the SUM of `calls`, in euros, written by `turnCostEur` like a
 *   turn's: it joins the cost of the session (§4.7), one figure with one source.
 * - `startedAt`, `settledAt` - the instant of the gesture that led to the
 *   launch (the arrival of the reply, or of the reconnection), not that of the
 *   first call, which `calls[0].startedAt` gives (N92); and the settlement. A
 *   determination that settles AFTER the session expired is recorded all the
 *   same (N25): the measure reads it, the person sees nothing of it.
 * - `analysis` - since issue #196 the REASONING of the two passes
 *   (`noyauAnalysisSchema`, analyste.md §10): the first pass objective by
 *   objective, the levels, the conflicts, the candidates and the eliminated, the
 *   compatibility, the style and its passages, the fingerprint of the
 *   instrument. NULL on a determination written before #196, which the parcours
 *   reads as « lecture directe, avant #196 » (K19); the six notes above are
 *   then the analyst's own, and with an analysis they are DERIVED by the code
 *   from it.
 */
export const noyauDeterminationSchema = z.object({
  state: noyauDeterminationStateSchema,
  scores: z.array(noyauScoreSchema).max(NOYAU_CODES.length).default([]),
  principal: noyauCodeSchema.nullable().default(null),
  second: noyauCodeSchema.nullable().default(null),
  draw: noyauDrawSchema.nullable().default(null),
  calls: z.array(noyauAnalystCallSchema).max(NOYAU_ANALYST_CALLS_MAX).default([]),
  costEur: z.number().nonnegative().default(0),
  startedAt: z.string().datetime(),
  settledAt: z.string().datetime().nullable().default(null),
  analysis: noyauAnalysisSchema.nullable().default(null),
});
export type NoyauDetermination = z.infer<typeof noyauDeterminationSchema>;

/**
 * ONE choice of a session (§5.1, §8): a row per mode, written at the FIRST
 * arrival on the page of choice and completed at the confirmation.
 *
 * - `mode` - the first choice (the card taken gives the RETAINED noyau within
 *   the threshold frozen on this row, §3.8) or the second (recorded, and it
 *   changes nothing).
 * - `pageId`, `blockId` - the page and the Noyau block, in the frozen version.
 * - `cards` - the PAIR, in the ORDER SHOWN, each card with its opaque id and the
 *   noyau it stands for: drawn at the first arrival, frozen for a reprise
 *   (N28), which is also what lets the measure check that no place is favoured.
 *   What a card WAS - the principal, the second, the retained, the opposite -
 *   follows from the determination and the first choice; when the opposite of
 *   the retained is the second of the first choice, the second choice replays
 *   the same pair on the second text, and is recorded as it is (N18).
 * - `shownAt` - the first arrival, when the order was drawn.
 * - `takenCardId`, `chosenAt` - the CONFIRMED card and the moment of the
 *   confirmation; null until then. The selections before it are not recorded
 *   (N30): the time spent on the page is read on the parcours already. The card
 *   taken is ONE OF THE TWO shown, and a row that says otherwise is refused: a
 *   retained noyau read off a card nobody was shown would be a noyau nobody chose.
 * - `maxGap` - since issue #169, the GAP THRESHOLD of the block, FROZEN on a
 *   first choice at its deal (noyau.md §3.8, N70): the one the retained noyau
 *   reads, never the threshold of a later version. Null on a first choice
 *   dealt before #169 - its card gives the retained noyau whatever the gap, and
 *   a session already played never changes its retained noyau (E4) - and null
 *   on a second choice, which changes nothing whatever its gap. A second choice
 *   that carries one is refused like a card nobody was shown: no writer
 *   produces it.
 */
export const noyauChoiceSchema = z
  .object({
    mode: noyauChoiceModeSchema,
    pageId: experienceLocalIdSchema,
    blockId: experienceLocalIdSchema,
    cards: noyauChoiceCardsSchema,
    shownAt: z.string().datetime(),
    takenCardId: noyauCardIdSchema.nullable().default(null),
    chosenAt: z.string().datetime().nullable().default(null),
    /** Read by default at the régime of BUILD-787: a row dealt before #169 has no threshold. */
    maxGap: noyauMaxGapSchema.nullable().default(null),
  })
  .refine(
    (choice) =>
      choice.takenCardId === null ||
      choice.cards.some((card) => card.cardId === choice.takenCardId),
    {
      message: 'The card taken is one of the two cards shown.',
      path: ['takenCardId'],
    },
  )
  .refine((choice) => choice.mode === 'first-choice' || choice.maxGap === null, {
    message: 'Only a first choice carries a gap threshold.',
    path: ['maxGap'],
  });
export type NoyauChoice = z.infer<typeof noyauChoiceSchema>;

/* ------------------------------------------------------------ the trial */

/**
 * A TRIAL of the analyst (noyau.md §9.4, issue #164): the analyst played on the
 * matter of a session already played, with the reading of the DEVELOPMENT
 * version of the protocol it played - the instrument being tuned - and shown to
 * the operator.
 *
 * It is NOT a determination, which is computed once and never again (§4.4,
 * §14): a trial writes nothing - no row, no call, no served entry, no cost -
 * and the determination a session may carry does not move. It lives in the
 * memory of the process that ran it and is forgotten after a while or at a
 * restart, when its address answers 404 and the operator runs it again.
 *
 * Its determination has the SHAPE of a stored one, so the parcours draws both
 * with one block: `pending` while the analyst runs, then `rendered` or `failed`
 * - the one retry of §4.5 played the same way. A session with no live extract is
 * refused before any call, so `no-extracts` never reaches a trial.
 */
export const noyauTrialStateSchema = noyauDeterminationStateSchema.exclude(['no-extracts']);
export type NoyauTrialState = z.infer<typeof noyauTrialStateSchema>;

/**
 * The reading a trial was served: the protocol the session played, at the
 * version its `development` pointer named when the trial started. The name is
 * that version's.
 */
export const noyauTrialReadingSchema = z.object({
  protocolId: z.string().min(1),
  name: z.string().min(1),
  versionId: z.string().min(1),
  versionNumber: z.number().int().positive(),
  tag: versionTagSchema,
});
export type NoyauTrialReading = z.infer<typeof noyauTrialReadingSchema>;

/**
 * What a trial READ on the targets of the rangement (relecture.md §11, issue
 * #187) - the SOURCE of its matter, and kept with it:
 *
 * - `session` - the matter of the session: its rangement when it is rendered,
 *   the scribe's extracts otherwise (the default, and what every trial played
 *   before the sources existed read);
 * - `rangement-trial` - the extracts of a blank rangement of the session
 *   (`trialId`, its `version` of the consigne and `startedAt`, enough to name it
 *   in « Essais de cette session » once that trial is purged or listed apart);
 * - `review` - the extracts of the REVIEW as they stood at `asOf`, the instant of
 *   the launch, `extractCount` of them: the review moves on and the trial must
 *   say what it read.
 */
export const noyauTrialSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('session') }),
  z.object({
    kind: z.literal('rangement-trial'),
    trialId: z.string().uuid(),
    version: z.number().int().positive(),
    startedAt: z.string().datetime(),
  }),
  z.object({
    kind: z.literal('review'),
    asOf: z.string().datetime(),
    extractCount: z.number().int().nonnegative(),
  }),
]);
export type NoyauTrialSource = z.infer<typeof noyauTrialSourceSchema>;

/** The source of a trial that names none: the matter of the session, as before. */
export const NOYAU_TRIAL_SOURCE_SESSION: NoyauTrialSource = { kind: 'session' };

/**
 * The source a trial is ASKED for (the body of `POST .../noyau/trials`): the
 * same three kinds, but where the stored one carries what was read, the request
 * names only what to read. `rangement-trial` names a trial that must be settled
 * and rendered (`rangement-trial-unusable` otherwise); `review` reads the
 * review at the instant of the launch.
 */
export const noyauTrialStartSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('session') }),
  z.object({ kind: z.literal('rangement-trial'), trialId: z.string().uuid() }),
  z.object({ kind: z.literal('review') }),
]);
export type NoyauTrialStartSource = z.infer<typeof noyauTrialStartSourceSchema>;

/**
 * The body of `POST /api/sessions/{id}/noyau/trials`. The body itself is
 * optional on the route - a bare press reads the matter of the session, as
 * before the sources existed - and so is its `source`.
 */
export const noyauTrialStartBodySchema = z.object({
  source: noyauTrialStartSourceSchema.default({ kind: 'session' }),
});
export type NoyauTrialStartBody = z.infer<typeof noyauTrialStartBodySchema>;

/**
 * `POST /api/sessions/{id}/noyau/trials` and its read,
 * `GET /api/sessions/{id}/noyau/trials/{trialId}`.
 *
 * - `reading` - which protocol version the analyst was served the reading of.
 * - `source` - what it read on the targets (`noyauTrialSourceSchema`); read by
 *   default as the matter of the session, which is what a trial written before
 *   the sources existed read.
 * - `determination` - the notes, the ranking and the calls, at the shape of a
 *   stored determination: `startedAt` is the press of the button, `settledAt`
 *   the answer, `costEur` the sum of the calls - counted nowhere else.
 * - `prompts` - what the analyst was served, call by call, in the order of the
 *   calls: kept with the trial since #167, served by the read of ONE trial and
 *   never by the list, which carries the person's matter only once.
 */
export const noyauTrialSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().min(1),
  reading: noyauTrialReadingSchema,
  source: noyauTrialSourceSchema.default(NOYAU_TRIAL_SOURCE_SESSION),
  determination: noyauDeterminationSchema.extend({ state: noyauTrialStateSchema }),
  prompts: z.array(noyauAnalystPromptSchema).max(NOYAU_ANALYST_CALLS_MAX).default([]),
});
export type NoyauTrial = z.infer<typeof noyauTrialSchema>;

/**
 * One trial as the LIST serves it (issue #167): the trial without what the
 * analyst was served - its largest part, and the person's matter a second
 * time -, which the read of that one trial carries.
 */
export const noyauTrialSummarySchema = noyauTrialSchema.omit({ prompts: true });
export type NoyauTrialSummary = z.infer<typeof noyauTrialSummarySchema>;

/**
 * `GET /api/sessions/{id}/noyau/trials` (issue #167) - the trials of a
 * session, newest first: the one running, if any, then those settled, which
 * are kept with the session and purged with it.
 */
export const noyauTrialListSchema = z.object({
  trials: z.array(noyauTrialSummarySchema),
});
export type NoyauTrialList = z.infer<typeof noyauTrialListSchema>;

/**
 * Why a trial is refused (409), before any call - the four ways a session has
 * nothing a trial could read, or no reading to read it with:
 *
 * - `no-protocol` - the session played no protocol that still reads, so there
 *   is no matter at all;
 * - `no-extracts` - it holds no live extract (§4.1): the analyst would read
 *   nothing, and a determination would have settled `no-extracts`;
 * - `protocol-retired` - the protocol it played is retired, so its development
 *   version is no longer anything's instrument;
 * - `analyst-reading-missing` - that development version carries no reading
 *   for the analyst, and there is none by default (§3.7, P5); since #196 it
 *   covers the reading of either pass (analyste.md §11.4);
 * - `analyst-levels-missing` - since #196, that development version has a level
 *   no objective carries (analyste.md §11.4): the first pass could not fix it,
 *   and the section is not one an instrument can be tuned on;
 * - `no-review-extracts` - the source is the review and it holds no extract
 *   (relecture.md §11);
 * - `rangement-trial-unusable` - the source names a blank rangement that is not
 *   one of this session's, or did not render (relecture.md §11).
 */
export const noyauTrialRefusalCodeSchema = z.enum([
  'no-protocol',
  'no-extracts',
  'protocol-retired',
  'analyst-reading-missing',
  'analyst-levels-missing',
  'no-review-extracts',
  'rangement-trial-unusable',
]);
export type NoyauTrialRefusalCode = z.infer<typeof noyauTrialRefusalCodeSchema>;

export const noyauTrialErrorSchema = z
  .object({
    error: z.string(),
    code: noyauTrialRefusalCodeSchema,
  })
  .strict();
export type NoyauTrialError = z.infer<typeof noyauTrialErrorSchema>;
